import { type FC, type ReactElement, useMemo } from "react";
import { styled } from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import Styles from "@/assets/styles/Styles";
import {
  buildTimeSeriesBuckets,
  calculateNumberOfPeriods,
  getIsoWeekNumber,
  type TimeSeriesPeriod,
  timestampsToDates,
} from "@/framework/KioForm/fields/PollResponseResultsField/timeseries";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const Container = styled("div")`
  width: 100%;
  height: 400px;
`;

const MILLISECONDS_PER_DAY = 86_400_000;

export type { TimeSeriesPeriod };

const decidePeriod = (firstDateTime: Date, lastDatetime: Date): TimeSeriesPeriod => {
  const numberOfDays = (lastDatetime.getTime() - firstDateTime.getTime()) / MILLISECONDS_PER_DAY;

  if (numberOfDays > 4 * 7 * 150) return "years";
  if (numberOfDays > 7 * 150) return "months";
  if (numberOfDays > 150) return "weeks";
  return "days";
};

const capitalize = (s: string): string => (s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s);

/** Short, human friendly tick label. Hides year when it matches current year. */
const formatPeriodLabel = (
  date: Date,
  period: TimeSeriesPeriod,
  locale: string,
  currentYear: number,
  t: (key: string) => string
): string => {
  const sameYear = date.getUTCFullYear() === currentYear;

  if (period === "years") return String(date.getUTCFullYear());

  if (period === "months") {
    const month = capitalize(new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(date));
    return sameYear ? month : `${month} ${date.getUTCFullYear()}`;
  }

  if (period === "weeks") {
    const weekLabel = `${t("polls.week")} ${getIsoWeekNumber(date)}`;
    return sameYear ? weekLabel : `${weekLabel}, ${date.getUTCFullYear()}`;
  }

  if (period === "days") {
    const dayMonth = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }).format(date);
    return sameYear ? dayMonth : `${dayMonth} ${date.getUTCFullYear()}`;
  }

  // hours
  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(date);
  const dayMonth = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(date);
  return `${dayMonth} ${time}`;
};

/** Longer label shown in tooltip. Always includes year and week range where applicable. */
const formatPeriodTooltip = (
  date: Date,
  period: TimeSeriesPeriod,
  locale: string,
  t: (key: string) => string
): string => {
  if (period === "years") return String(date.getUTCFullYear());

  if (period === "months") {
    return capitalize(
      new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(date)
    );
  }

  if (period === "weeks") {
    const end = new Date(date);
    end.setUTCDate(date.getUTCDate() + 6);
    const range = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
    return `${t("polls.week")} ${getIsoWeekNumber(date)}, ${date.getUTCFullYear()} (${range.format(
      date
    )} – ${range.format(end)})`;
  }

  if (period === "days") {
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  }

  // hours
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(date);
};

interface PollResponseTimeSeriesPlotProps {
  responseLog?: string[];
  periodOverride?: TimeSeriesPeriod;
  width?: number;
  height?: number;
}

const PollResponseTimeSeriesPlot: FC<PollResponseTimeSeriesPlotProps> = ({
  responseLog = [],
  periodOverride,
}): ReactElement => {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.language || "en";

  const sortedDateTimes = useMemo(
    () => timestampsToDates(responseLog).toSorted((a, b) => a.getTime() - b.getTime()),
    [responseLog]
  );

  const period: TimeSeriesPeriod = periodOverride
    ? periodOverride
    : sortedDateTimes.length > 0
    ? decidePeriod(sortedDateTimes[0], sortedDateTimes.at(-1)!)
    : "days";

  const { labels, tooltipTitles, counts } = useMemo(() => {
    const numPeriods = calculateNumberOfPeriods(sortedDateTimes, period);
    const buckets = buildTimeSeriesBuckets(sortedDateTimes, period, numPeriods);
    const currentYear = new Date().getFullYear();
    return {
      labels: buckets.map((b) => formatPeriodLabel(b.date, period, locale, currentYear, t)),
      tooltipTitles: buckets.map((b) => formatPeriodTooltip(b.date, period, locale, t)),
      counts: buckets.map((b) => b.count),
    };
  }, [sortedDateTimes, period, locale, t]);

  const data = {
    labels,
    datasets: [
      {
        label: t("polls.count") as string,
        data: counts,
        borderColor: Styles.Colors.THEME_PRIMARY,
        backgroundColor: Styles.Colors.THEME_PRIMARY,
        pointBackgroundColor: Styles.Colors.THEME_PRIMARY,
        pointRadius: 3,
        borderWidth: 1.5,
        tension: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        intersect: false,
        mode: "index" as const,
        callbacks: {
          title: (items: { dataIndex: number }[]) => (items.length > 0 ? tooltipTitles[items[0].dataIndex] : ""),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 7,
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        grid: { color: "rgba(0,0,0,0.08)" },
      },
    },
  };

  return (
    <Container>
      <Line data={data} options={options} />
    </Container>
  );
};

export default PollResponseTimeSeriesPlot;
